import { computed, onBeforeUnmount, ref, unref, watch } from 'vue'
import {
  getReplayInventory,
  getReplayVersions,
  preflightReplay,
  confirmReplay,
  retryReplayVersion,
  regenerateReplayOutput,
} from '@/api/meetingReplay'

const data = (response) => {
  if (![0, 200, '0', '200'].includes(response?.code))
    throw new Error(response?.message || response?.msg || '请求失败，请重试')
  return response.data
}
const key = () => window.crypto?.randomUUID?.() || `replay-${Date.now()}-${Math.random().toString(16).slice(2)}`
export const playable = (version) => version?.outputs?.some((o) => o.status === 'COMPLETED' && o.videoUrl)
export const bestReplayVersion = (versions) =>
  versions.find((v) => v.outputs?.some((o) => o.id !== 'baseline' && o.status === 'COMPLETED' && o.videoUrl)) ||
  versions.find(playable)

export const mergeReplayVersions = (current, historical = []) => {
  const versions = new Map()
  for (const version of [...historical, ...current]) versions.set(version.generationId, version)
  return [...versions.values()].sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0))
}
const working = (version) => version.workActive || ['QUEUED', 'PROCESSING', 'RETRY_WAIT'].includes(version.status)

export function initialReplayRequest(assets, versions) {
  const captures = assets.captures || []
  const editable = versions.filter((version) => version.ranges?.length)
  const previous = editable.find((version) => version.generationIntent === 'FULL') || editable[0]
  const known = new Set(captures.map((capture) => capture.captureSessionId))
  const explicit =
    previous?.sourceSelectionMode === 'EXPLICIT' ||
    (!previous?.sourceSelectionMode && previous?.captureSessionIds?.length > 0)
  return {
    sourceSelectionMode: explicit ? 'EXPLICIT' : 'AUTO',
    pausePolicy: previous ? previous.pausePolicy || 'PRESERVE' : 'SKIP_BREAKS',
    captureSessionIds: explicit ? (previous.captureSessionIds || []).filter((id) => known.has(id)) : [],
    ranges: previous?.ranges?.length
      ? previous.ranges.map((range) => ({ ...range }))
      : [
          {
            startAtMs: captures.length ? Math.min(...captures.map((capture) => capture.startAtMs)) : null,
            endAtMs: captures.length ? Math.max(...captures.map((capture) => capture.endAtMs)) : null,
          },
        ],
    languages: [],
    bannerIndex: null,
    inheritPreviousPpt: false,
    generationIntent: 'PARTIAL',
  }
}

export function useReplayGeneration(meetingCode) {
  const inventory = ref(null),
    versions = ref([]),
    preflight = ref(null),
    loading = ref(false),
    busy = ref(false),
    error = ref('')
  let epoch = 0,
    timer = null,
    refreshSequence = 0,
    idempotencyKey = '',
    lastInventoryAt = 0
  const code = () => String(unref(meetingCode) || '')
  const current = (scope, meeting) => scope === epoch && meeting === code()
  const active = computed(() => versions.value.find(working))
  const generationBusy = computed(
    () =>
      active.value ||
      versions.value.some((v) => v.generationBusy) ||
      (!versions.value.length && inventory.value?.generationBusy)
  )
  const outputKeys = new Map()
  const refresh = async (forceInventory = true) => {
    const scope = epoch,
      meeting = code(),
      sequence = ++refreshSequence
    if (!meeting || busy.value) return
    clearTimeout(timer)
    loading.value = !inventory.value
    try {
      const fetchInventory = forceInventory || Date.now() - lastInventoryAt > 30000
      const [list, assets] = await Promise.all([
        getReplayVersions(meeting),
        fetchInventory ? getReplayInventory(meeting) : null,
      ])
      if (!current(scope, meeting) || sequence !== refreshSequence) return
      if (assets) {
        inventory.value = data(assets)
        lastInventoryAt = Date.now()
      }
      versions.value = mergeReplayVersions(data(list) || [], inventory.value?.legacyVersions || [])
      error.value = ''
    } catch (e) {
      if (current(scope, meeting) && sequence === refreshSequence) error.value = e.message || '加载失败'
    } finally {
      if (current(scope, meeting) && sequence === refreshSequence) {
        loading.value = false
        timer = setTimeout(() => refresh(false), active.value ? 5000 : 30000)
      }
    }
  }
  const act = async (work) => {
    if (busy.value) return null
    const scope = epoch,
      meeting = code()
    busy.value = true
    error.value = ''
    clearTimeout(timer)
    ++refreshSequence
    try {
      const result = data(await work(meeting))
      return current(scope, meeting) ? result : null
    } catch (e) {
      if (current(scope, meeting)) error.value = e.message || '操作失败，请重试'
      return null
    } finally {
      if (current(scope, meeting)) {
        busy.value = false
        timer = setTimeout(() => refresh(false), 5000)
      }
    }
  }
  const inspect = async (request) => {
    preflight.value = null
    const result = await act((meeting) => preflightReplay(meeting, request))
    if (result) {
      preflight.value = result
      idempotencyKey = key()
    }
    return result
  }
  const confirm = async () => {
    const plan = preflight.value
    if (!plan || generationBusy.value) return null
    // Keep this key after a timeout: retrying confirmation cannot create another version.
    const result = await act((meeting) =>
      confirmReplay(
        meeting,
        {
          preflightId: plan.preflightId,
          planVersion: plan.planVersion,
          assetRevision: plan.assetRevision,
        },
        idempotencyKey
      )
    )
    if (result) {
      preflight.value = null
      await refresh(true)
    }
    return result
  }
  const retry = async (id) => {
    if (generationBusy.value) return null
    const result = await act((meeting) => retryReplayVersion(meeting, id))
    if (result) await refresh(false)
  }
  const regenerate = async (id, outputId) => {
    const target = versions.value.find((v) => v.generationId === id)?.outputs?.find((o) => o.id === outputId)
    if (!target?.canRegenerate) return null
    const identity = `${code()}/${id}/${outputId}`
    if (!outputKeys.has(identity)) outputKeys.set(identity, key())
    const result = await act((meeting) => regenerateReplayOutput(meeting, id, outputId, outputKeys.get(identity)))
    if (result) {
      outputKeys.delete(identity)
      await refresh(false)
    }
    return result
  }
  const discard = () => {
    preflight.value = null
  }
  watch(
    () => code(),
    () => {
      ++epoch
      ++refreshSequence
      clearTimeout(timer)
      inventory.value = null
      versions.value = []
      preflight.value = null
      busy.value = false
      loading.value = false
      error.value = ''
      lastInventoryAt = 0
      outputKeys.clear()
      refresh()
    },
    { immediate: true }
  )
  onBeforeUnmount(() => {
    ++epoch
    clearTimeout(timer)
  })
  return {
    inventory,
    versions,
    preflight,
    loading,
    busy,
    error,
    active,
    generationBusy,
    refresh,
    inspect,
    confirm,
    retry,
    regenerate,
    discard,
  }
}
